// src/legal/LegalText.tsx: the text of a legal document read by markdown.ts (inline runs, lists, tables, subheads), as
// the privacy notice (/privacy, PrivacyScreen.tsx) and the agreements panel (src/team/AgreementSheet.tsx) show it.
// [TO CONFIRM] placeholders stay visible as subtle highlighted marks until they are answered. Styles: legal.css.
import { plain, type Block, type Inline, type ListItem } from "./markdown";
import "../styles/legal.css";

export function Runs(props: { runs: Inline[] }): preact.JSX.Element {
  return (
    <>
      {props.runs.map((r, i) =>
        r.kind === "bold" ? (
          <b key={i}>
            <Runs runs={r.children ?? [{ kind: "text", text: r.text }]} />
          </b>
        ) : r.kind === "code" ? (
          <code key={i}>{r.text}</code>
        ) : r.kind === "tbc" ? (
          <mark key={i} class="tbc" title="A placeholder, to be confirmed before this notice is published">
            <span class="tbc-k">{r.text ? "To confirm: " : "To confirm"}</span>
            {r.text}
          </mark>
        ) : (
          <span key={i}>{r.text}</span>
        ),
      )}
    </>
  );
}

export function Items(props: { items: ListItem[]; nested?: boolean }) {
  return (
    <ul class={props.nested ? "pv-ul pv-ul-n" : "pv-ul"}>
      {props.items.map((it, i) => (
        <li key={i}>
          <Runs runs={it.text} />
          {it.children.length ? <Items items={it.children} nested /> : null}
        </li>
      ))}
    </ul>
  );
}

export function Blocks(props: { blocks: Block[] }) {
  return (
    <>
      {props.blocks.map((b, i) => {
        if (b.kind === "p") {
          return (
            <p key={i} class="pv-p">
              <Runs runs={b.text} />
            </p>
          );
        }
        if (b.kind === "sub") {
          return (
            <h3 key={i} class="pv-h3">
              <Runs runs={b.text.flatMap((r) => (r.kind === "bold" ? (r.children ?? []) : [r]))} />
            </h3>
          );
        }
        if (b.kind === "ul") return <Items key={i} items={b.items} />;
        const labels = b.head.map((h) => plain(h));
        return (
          <div key={i} class="pv-table">
            <table>
              <thead>
                <tr>
                  {b.head.map((h, j) => (
                    <th key={j} scope="col">
                      <Runs runs={h} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((c, j) =>
                      j === 0 ? (
                        <th key={j} scope="row">
                          <Runs runs={c} />
                        </th>
                      ) : (
                        <td key={j} data-label={labels[j] ?? ""}>
                          <Runs runs={c} />
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}
